import React, { useEffect, useState } from "react";
import axios from "axios";
import { Save, RotateCcw, RefreshCw, BedDouble, Bath, ChefHat, Building2, Percent, Clock, Shirt } from "lucide-react";
import { Toaster } from "../ui/sonner";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-");

const OVEN_FIELDS = [
  ["base", "Base price (single oven)"],
  ["doubleOven", "Double oven"],
  ["agaRange", "AGA / range cooker"],
  ["hob", "Hob cleaning"],
  ["extractorFan", "Extractor fan"],
];

const NumberField = ({ label, value, onChange, prefix = "£", suffix, testId }) => (
  <label className="flex flex-col gap-1 text-xs font-semibold text-slate-500">
    {label}
    <div className="flex items-center rounded-md border border-slate-200 bg-white focus-within:ring-2 focus-within:ring-sky-500">
      {prefix && <span className="pl-1.5 text-slate-400">{prefix}</span>}
      <input
        type="number"
        min="0"
        value={value}
        onChange={(e) => onChange(e.target.value === "" ? "" : Math.max(0, parseInt(e.target.value, 10) || 0))}
        onBlur={() => value === "" && onChange(0)}
        className="no-spinner w-full min-w-0 bg-transparent px-1.5 py-2 text-sm font-semibold text-slate-900 outline-none"
        data-testid={testId}
      />
      {suffix && <span className="pr-2 text-slate-400">{suffix}</span>}
    </div>
  </label>
);

const Card = ({ icon: Icon, title, hint, children, testId }) => (
  <section className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm" data-testid={testId}>
    <div className="mb-4 flex items-start gap-3">
      <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-sky-50 text-sky-600">
        <Icon className="h-5 w-5" />
      </div>
      <div>
        <h3 className="text-base font-bold text-slate-900">{title}</h3>
        {hint && <p className="text-xs text-slate-500">{hint}</p>}
      </div>
    </div>
    {children}
  </section>
);

const BedroomPrices = ({ pricing, update }) => (
  <Card icon={BedDouble} title="Bedroom prices" hint="Price per visit by number of bedrooms (before discounts)" testId="pricing-bedroom-card">
    <div className="space-y-4">
      {Object.entries(pricing.basePrices).map(([service, prices]) => (
        <div key={service}>
          <div className="mb-2 text-sm font-bold text-slate-700">{service}</div>
          <div className="grid grid-cols-4 gap-2 sm:grid-cols-8">
            {prices.map((p, i) => (
              <NumberField
                key={i}
                label={`${i + 1} bed`}
                value={p}
                testId={`pricing-base-${slug(service)}-${i + 1}`}
                onChange={(v) => update((d) => { d.basePrices[service][i] = v; })}
              />
            ))}
          </div>
        </div>
      ))}
    </div>
  </Card>
);

const RoomExtras = ({ pricing, update }) => (
  <Card icon={Bath} title="Extra rooms" hint="First bathroom is included. Each extra bathroom and each separate toilet adds:" testId="pricing-room-extras-card">
    <div className="space-y-3">
      {Object.entries(pricing.roomExtras).map(([service, ex]) => (
        <div key={service} className="grid grid-cols-[1fr_auto_auto] items-end gap-2 sm:gap-3">
          <div className="pb-2 text-sm font-bold text-slate-700">{service}</div>
          <div className="w-24"><NumberField label="Bathroom" value={ex.bathroom} testId={`pricing-extra-bathroom-${slug(service)}`} onChange={(v) => update((d) => { d.roomExtras[service].bathroom = v; })} /></div>
          <div className="w-24"><NumberField label="Toilet" value={ex.toilet} testId={`pricing-extra-toilet-${slug(service)}`} onChange={(v) => update((d) => { d.roomExtras[service].toilet = v; })} /></div>
        </div>
      ))}
    </div>
  </Card>
);

const OvenAndCommercial = ({ pricing, update }) => (
  <>
    <Card icon={ChefHat} title="Oven cleaning" hint="Base price plus each selected add-on" testId="pricing-oven-card">
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
        {OVEN_FIELDS.map(([key, label]) => (
          <NumberField key={key} label={label} value={pricing.oven[key]} testId={`pricing-oven-${key}`} onChange={(v) => update((d) => { d.oven[key] = v; })} />
        ))}
      </div>
    </Card>
    <Card icon={Building2} title="Commercial cleaning" hint="Base covers up to 1,000 sq ft" testId="pricing-commercial-card">
      <div className="grid grid-cols-2 gap-3">
        <NumberField label="Base price" value={pricing.commercial.base} testId="pricing-commercial-base" onChange={(v) => update((d) => { d.commercial.base = v; })} />
        <NumberField label="Per extra 1,000 sq ft" value={pricing.commercial.perExtra1000SqFt} testId="pricing-commercial-per1000" onChange={(v) => update((d) => { d.commercial.perExtra1000SqFt = v; })} />
      </div>
    </Card>
  </>
);

const LAUNDRY_FIELDS = [
  ["perKg", "Wash, dry & fold (per kg)"],
  ["ironingItem", "Ironing (per item)"],
  ["duvet", "Duvet (each)"],
  ["suitDryClean", "2-piece suit dry clean"],
];

const LaundryPrices = ({ pricing, update }) => (
  <Card icon={Shirt} title="Laundry & ironing" hint="Used by the calculator and the laundry page" testId="pricing-laundry-card">
    <div className="grid grid-cols-2 gap-3">
      {LAUNDRY_FIELDS.map(([key, label]) => (
        <NumberField key={key} label={label} value={pricing.laundry[key]} testId={`pricing-laundry-${key}`} onChange={(v) => update((d) => { d.laundry[key] = v; })} />
      ))}
    </div>
  </Card>
);

const CARPET_FIELDS = [
  ["room", "Bedroom / room"],
  ["livingRoom", "Living room (flat)"],
  ["stairsHallway", "Stairs / hallway"],
  ["stainProtection", "Stain / pet odour add-on"],
];

const CarpetPrices = ({ pricing, update }) => (
  <Card icon={BedDouble} title="Carpet cleaning" hint="Used by the calculator" testId="pricing-carpet-card">
    <div className="grid grid-cols-2 gap-3">
      {CARPET_FIELDS.map(([key, label]) => (
        <NumberField key={key} label={label} value={pricing.carpet[key]} testId={`pricing-carpet-${key}`} onChange={(v) => update((d) => { d.carpet[key] = v; })} />
      ))}
    </div>
  </Card>
);

const DiscountsAndTime = ({ pricing, update }) => (
  <>
    <Card icon={Percent} title="Frequency discounts" testId="pricing-discounts-card">
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        {Object.entries(pricing.discounts).map(([freq, pct]) => (
          <NumberField key={freq} label={freq} value={pct} prefix="" suffix="%" testId={`pricing-discount-${slug(freq)}`} onChange={(v) => update((d) => { d.discounts[freq] = Math.min(v, 90); })} />
        ))}
      </div>
    </Card>
    <Card icon={Clock} title="Extra cleaning time" hint="Added to the estimated duration" testId="pricing-time-card">
      <div className="grid grid-cols-2 gap-3">
        <NumberField label="Per extra bathroom" value={pricing.timeExtras.bathroomMinutes} prefix="" suffix="min" testId="pricing-time-bathroom" onChange={(v) => update((d) => { d.timeExtras.bathroomMinutes = Math.min(v, 240); })} />
        <NumberField label="Per separate toilet" value={pricing.timeExtras.toiletMinutes} prefix="" suffix="min" testId="pricing-time-toilet" onChange={(v) => update((d) => { d.timeExtras.toiletMinutes = Math.min(v, 240); })} />
      </div>
    </Card>
  </>
);

const Toolbar = ({ dirty, saving, onSave, onReset }) => (
  <div className="sticky top-16 z-30 -mx-4 flex flex-wrap items-center justify-between gap-3 border-b border-slate-200 bg-slate-50/95 px-4 py-3 backdrop-blur">
    <p className="text-sm text-slate-600" data-testid="pricing-status">
      {dirty ? "You have unsaved changes" : "Prices are live on the website calculator"}
    </p>
    <div className="flex gap-2">
      <button onClick={onReset} disabled={saving} data-testid="pricing-reset-btn" className="inline-flex items-center gap-2 rounded-md border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-600 transition-colors hover:bg-slate-100 disabled:opacity-60">
        <RotateCcw className="h-4 w-4" /> Reset to defaults
      </button>
      <button onClick={onSave} disabled={!dirty || saving} data-testid="pricing-save-btn" className="inline-flex items-center gap-2 rounded-md bg-sky-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-sky-700 disabled:opacity-50">
        {saving ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save prices
      </button>
    </div>
  </div>
);

const AdminPricingEditor = () => {
  const [pricing, setPricing] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get(`${API}/pricing`).then((r) => setPricing(r.data)).catch(() => toast.error("Could not load prices"));
  }, []);

  const update = (mutate) => {
    setPricing((prev) => {
      const next = structuredClone(prev);
      mutate(next);
      return next;
    });
    setDirty(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      const r = await axios.put(`${API}/admin/pricing`, pricing, { withCredentials: true });
      setPricing(r.data);
      setDirty(false);
      toast.success("Prices saved and live on the website");
    } catch (err) {
      toast.error(err?.response?.status === 401 ? "Session expired - please log in again" : "Could not save prices");
    } finally {
      setSaving(false);
    }
  };

  const reset = async () => {
    if (!window.confirm("Reset all prices to the original defaults?")) return;
    setSaving(true);
    try {
      const r = await axios.post(`${API}/admin/pricing/reset`, {}, { withCredentials: true });
      setPricing(r.data);
      setDirty(false);
      toast.success("Prices reset to defaults");
    } catch {
      toast.error("Could not reset prices");
    } finally {
      setSaving(false);
    }
  };

  if (!pricing) {
    return <div className="py-16 text-center text-slate-500" data-testid="pricing-loading">Loading prices...</div>;
  }

  return (
    <div className="space-y-6" data-testid="admin-pricing-editor">
      <Toaster position="bottom-right" richColors />
      <Toolbar dirty={dirty} saving={saving} onSave={save} onReset={reset} />
      <BedroomPrices pricing={pricing} update={update} />
      <div className="grid gap-6 lg:grid-cols-2">
        <RoomExtras pricing={pricing} update={update} />
        <div className="space-y-6"><DiscountsAndTime pricing={pricing} update={update} /></div>
      </div>
      <div className="grid gap-6 lg:grid-cols-2"><OvenAndCommercial pricing={pricing} update={update} /></div>
      <div className="grid gap-6 lg:grid-cols-2">
        <LaundryPrices pricing={pricing} update={update} />
        <CarpetPrices pricing={pricing} update={update} />
      </div>
    </div>
  );
};

export default AdminPricingEditor;
